import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';

import * as ApplicationInfrastructureService
    from '../services/ApplicationInfrastructureService';
import {
    CreateApplicationInfrastructureInput,
    UpdateApplicationInfrastructureInput,
} from '../types-infrastructure';
import {notifyError, notifySuccess} from '../../../components/PlatformNotification';

// FNP-1016: React Query surface for the infrastructure inventory.

export const applicationInfrastructureKeys = {
    all: ['application-infrastructure'] as const,
    lists: () => [...applicationInfrastructureKeys.all, 'list'] as const,
    list: (applicationId: string) =>
        [...applicationInfrastructureKeys.lists(), applicationId] as const,
    details: () => [...applicationInfrastructureKeys.all, 'detail'] as const,
    detail: (id: string) => [...applicationInfrastructureKeys.details(), id] as const,
};

// Per-application infrastructure records number in the single digits (one per
// provisioning act), so this is a plain query rather than an infinite one — the
// list is bounded by the entity's own append-only-per-act shape, not by a page
// size. `nextToken` is still selected and returned so a future caller can page
// if that assumption ever stops holding.
export const useApplicationInfrastructureListQuery = (applicationId?: string) => {
    return useQuery({
        queryKey: applicationInfrastructureKeys.list(applicationId ?? ''),
        queryFn: () => ApplicationInfrastructureService.fetchRecords(applicationId ?? ''),
        enabled: !!applicationId,
    });
};

export const useApplicationInfrastructureQuery = (id?: string) => {
    return useQuery({
        queryKey: applicationInfrastructureKeys.detail(id ?? ''),
        queryFn: () => ApplicationInfrastructureService.fetch(id ?? ''),
        enabled: !!id,
    });
};

// ⚠️ The success message says RECORDED, not provisioned or created. This
// mutation writes a row; it builds nothing. An admin who reads "infrastructure
// created" and believes AWS resources were just made is the serious failure
// this wording exists to prevent.
export const useCreateApplicationInfrastructureMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (input: CreateApplicationInfrastructureInput) =>
            ApplicationInfrastructureService.add(input),
        onSuccess: (_data, input) => {
            notifySuccess(
                'Existing infrastructure recorded. No AWS resources were created or '
                + 'changed — this records what is already there.',
            );
            queryClient.invalidateQueries({
                queryKey: applicationInfrastructureKeys.list(input.applicationId),
            });
        },
        onError: notifyError,
    });
};

export const useUpdateApplicationInfrastructureMutation = (applicationId?: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (input: UpdateApplicationInfrastructureInput) =>
            ApplicationInfrastructureService.update(input),
        onSuccess: (_data, input) => {
            notifySuccess('Infrastructure record updated. No AWS resources were changed.');
            queryClient.invalidateQueries({
                queryKey: applicationInfrastructureKeys.detail(input.id),
            });
            if (applicationId) {
                queryClient.invalidateQueries({
                    queryKey: applicationInfrastructureKeys.list(applicationId),
                });
            }
        },
        onError: notifyError,
    });
};
